import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Ticket, 
  Play, 
  RotateCcw, 
  Users, 
  Trophy, 
  Volume2, 
  VolumeX, 
  Maximize, 
  Minimize, 
  Shuffle, 
  Copy, 
  Check, 
  Trash2
} from 'lucide-react';
import { Card, Button, Badge } from '../ui';
import { useNodeRoster } from '../../hooks/useNodeRoster';
import { NodeRosterPickerModal } from './common/NodeRosterPickerModal';
import { DirectoryMember } from '../../types';
import toast from 'react-hot-toast';

const DEFAULT_PARTICIPANTS = [
  'John Doe',
  'Jane Smith',
  'Michael Johnson',
  'Sarah Davis',
  'Emily Wilson',
  'David Brown',
].join('\n');

const playTickAudio = (ctx: AudioContext, time: number) => {
  try {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(850 + Math.random() * 150, time);
    osc.frequency.exponentialRampToValueAtTime(250, time + 0.025);
    gain.gain.setValueAtTime(0.09, time);
    gain.gain.exponentialRampToValueAtTime(0.001, time + 0.025);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(time);
    osc.stop(time + 0.025);
  } catch {
    // Audio scheduling safely ignored
  }
};

const playWinnerChime = (ctx: AudioContext) => {
  try {
    const now = ctx.currentTime;
    const freqs = [523.25, 659.25, 783.99, 1046.50]; // C5, E5, G5, C6
    freqs.forEach((f, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = f;
      const start = now + (i * 0.1);
      gain.gain.setValueAtTime(0.15, start);
      gain.gain.exponentialRampToValueAtTime(0.001, start + 0.9);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(start);
      osc.stop(start + 0.9);
    });
  } catch {
    // Audio error safely ignored
  }
};

export function DigitalRaffleDrum() {
  const [inputText, setInputText] = useState(DEFAULT_PARTICIPANTS);
  const [participants, setParticipants] = useState<string[]>([]);
  const [gameState, setGameState] = useState<'setup' | 'spinning' | 'winner'>('setup');
  const [winner, setWinner] = useState<string | null>(null);
  const [winnerHistory, setWinnerHistory] = useState<string[]>([]);
  const [targetIndex, setTargetIndex] = useState(0);
  const [spinCount, setSpinCount] = useState(0);
  const [isSoundOn, setIsSoundOn] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [copiedWinners, setCopiedWinners] = useState(false);
  const [isRosterModalOpen, setIsRosterModalOpen] = useState(false);

  const rootRef = useRef<HTMLDivElement>(null);
  const spinTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);

  const { hasActiveNode, nodeName } = useNodeRoster();

  // We need a long array to create the visual "spinning" effect
  const [visualTrack, setVisualTrack] = useState<string[]>([]);

  useEffect(() => {
    return () => {
      if (spinTimeoutRef.current) {
        clearTimeout(spinTimeoutRef.current);
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close().catch(() => {});
      }
    };
  }, []);

  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  const parsedNames = useMemo(() => {
    return inputText
      .split('\n')
      .map(n => n.trim())
      .filter(n => n.length > 0);
  }, [inputText]);

  const entryCount = parsedNames.length;
  const uniqueCount = useMemo(() => new Set(parsedNames).size, [parsedNames]);

  const toggleFullscreen = React.useCallback(() => {
    if (!rootRef.current) return;
    if (!document.fullscreenElement) {
      rootRef.current.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen().catch(() => {});
    }
  }, []);

  const scheduleSpinSound = React.useCallback(() => {
    if (!isSoundOn) return;
    try {
      if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
        const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
        if (AudioCtx) {
          audioContextRef.current = new AudioCtx();
        }
      }
      const ctx = audioContextRef.current;
      if (!ctx) return;
      if (ctx.state === 'suspended') {
        ctx.resume().catch(() => {});
      }

      const now = ctx.currentTime;
      const totalDuration = 5.0;
      const numTicks = 42;
      for (let i = 0; i < numTicks; i++) {
        const progress = i / numTicks;
        const t = totalDuration * (1 - Math.pow(1 - progress, 2.3));
        playTickAudio(ctx, now + t);
      }
    } catch {
      // Audio error safely ignored
    }
  }, [isSoundOn]);

  const scheduleWinnerSound = () => {
    if (!isSoundOn || !audioContextRef.current) return;
    playWinnerChime(audioContextRef.current);
  };

  const startRaffle = React.useCallback((overrideNames?: string[]) => {
    const namesToUse = overrideNames || parsedNames;
    if (namesToUse.length < 2) return;

    const names = [...namesToUse];
    setParticipants(names);

    // Pick winner first so we know where to stop
    const winningIndex = Math.floor(Math.random() * names.length);
    const winningName = names[winningIndex];
    setWinner(winningName);

    // We ALWAYS spin exactly 100 items for the perfect 5-second animation speed.
    const targetStopIndex = 100;

    // Create a track of random names, avoiding back-to-back duplicates when possible
    const track = new Array(targetStopIndex + 10);
    track[targetStopIndex] = winningName;

    // Fill backwards from the winner
    for (let i = targetStopIndex - 1; i >= 0; i--) {
      const available = names.filter(n => n !== track[i + 1]);
      const pool = available.length > 0 ? available : names;
      track[i] = pool[Math.floor(Math.random() * pool.length)];
    }

    // Fill forwards from the winner
    for (let i = targetStopIndex + 1; i < track.length; i++) {
      const available = names.filter(n => n !== track[i - 1]);
      const pool = available.length > 0 ? available : names;
      track[i] = pool[Math.floor(Math.random() * pool.length)];
    }

    setVisualTrack(track);
    setTargetIndex(targetStopIndex);
    setSpinCount(prev => prev + 1);
    setGameState('spinning');
    scheduleSpinSound();
  }, [parsedNames, scheduleSpinSound]);

  const handleAnimationComplete = () => {
    if (gameState === 'spinning' && winner) {
      scheduleWinnerSound();
      setWinnerHistory(prev => [winner, ...prev]);
      if (spinTimeoutRef.current) {
        clearTimeout(spinTimeoutRef.current);
      }
      spinTimeoutRef.current = setTimeout(() => {
        setGameState('winner');
      }, 500);
    }
  };

  const resetRaffle = () => {
    if (spinTimeoutRef.current) {
      clearTimeout(spinTimeoutRef.current);
    }
    setGameState('setup');
    setWinner(null);
    setTargetIndex(0);
  };

  const removeWinnerAndRerun = React.useCallback(() => {
    if (spinTimeoutRef.current) {
      clearTimeout(spinTimeoutRef.current);
    }
    if (!winner) return;

    // Remove only one instance of the winning ticket
    const winnerIdx = participants.indexOf(winner);
    const remaining = [...participants];
    if (winnerIdx !== -1) {
      remaining.splice(winnerIdx, 1);
    }
    setParticipants(remaining);
    setInputText(remaining.join('\n'));
    setWinner(null);
    setTargetIndex(0);
    
    if (remaining.length >= 2) {
      startRaffle(remaining);
    } else {
      setGameState('setup');
    }
  }, [winner, participants, startRaffle]);

  const spinAgainKeepWinner = () => {
    if (spinTimeoutRef.current) {
      clearTimeout(spinTimeoutRef.current);
    }
    setWinner(null);
    setTargetIndex(0);
    if (participants.length >= 2) {
      startRaffle(participants);
    } else {
      setGameState('setup');
    }
  };

  const handleShuffle = () => {
    const list = [...parsedNames];
    for (let i = list.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [list[i], list[j]] = [list[j], list[i]];
    }
    setInputText(list.join('\n'));
    toast.success('Attendee list shuffled');
  };

  const handleImportFromNode = (selectedMembers: DirectoryMember[]) => {
    if (selectedMembers.length === 0) return;
    const names = selectedMembers.map(m => m.name).filter(Boolean);
    const existing = inputText.trim() ? inputText.trim() + '\n' : '';
    setInputText(existing + names.join('\n'));
    toast.success(`Imported ${names.length} attendees from ${nodeName || 'organization'}`);
  };

  const copyWinnerHistory = () => {
    if (winnerHistory.length === 0) return;
    const text = winnerHistory.map((w, i) => `${i + 1}. ${w}`).join('\n');
    navigator.clipboard.writeText(text);
    setCopiedWinners(true);
    toast.success('Winner list copied to clipboard');
    setTimeout(() => setCopiedWinners(false), 2000);
  };

  // Global keyboard shortcuts (Space to spin/restart, F for fullscreen, M for mute)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const activeTag = document.activeElement?.tagName.toLowerCase();
      if (activeTag === 'textarea' || activeTag === 'input') return;

      if (e.code === 'Space') {
        e.preventDefault();
        if (gameState === 'setup' && entryCount >= 2) {
          startRaffle();
        } else if (gameState === 'winner') {
          removeWinnerAndRerun();
        }
      } else if (e.key === 'f' || e.key === 'F') {
        toggleFullscreen();
      } else if (e.key === 'm' || e.key === 'M') {
        setIsSoundOn(s => !s);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [gameState, entryCount, startRaffle, removeWinnerAndRerun, toggleFullscreen]);

  return (
    <div 
      ref={rootRef}
      className={`max-w-6xl mx-auto p-4 space-y-8 ${isFullscreen ? 'fixed inset-0 z-50 bg-slate-950 p-8 max-w-none flex flex-col justify-center overflow-auto' : ''}`}
    >
      {/* Control Bar: Fullscreen & Sound */}
      <div className="flex items-center justify-between bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl px-4 py-2 text-xs font-bold text-slate-600 dark:text-slate-400">
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1.5 font-mono">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
            Projector Ready
          </span>
          <span className="hidden sm:inline text-slate-400 dark:text-slate-600">|</span>
          <span className="hidden sm:inline text-slate-500">Shortcuts: Space (Spin), F (Fullscreen), M (Mute)</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setIsSoundOn(!isSoundOn)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
            title={isSoundOn ? 'Mute Sound (M)' : 'Enable Sound (M)'}
          >
            {isSoundOn ? <Volume2 className="w-3.5 h-3.5 text-amber-500" /> : <VolumeX className="w-3.5 h-3.5 text-slate-400" />}
            <span>{isSoundOn ? 'Sound On' : 'Muted'}</span>
          </button>
          <button
            type="button"
            onClick={toggleFullscreen}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
            title={isFullscreen ? 'Exit Fullscreen (F)' : 'Enter Fullscreen (F)'}
          >
            {isFullscreen ? <Minimize className="w-3.5 h-3.5 text-amber-500" /> : <Maximize className="w-3.5 h-3.5 text-slate-600 dark:text-slate-400" />}
            <span>{isFullscreen ? 'Exit Fullscreen' : 'Big Screen'}</span>
          </button>
        </div>
      </div>

      {/* Header (Hidden in Fullscreen) */}
      {!isFullscreen && (
        <div className="bg-gradient-to-r from-amber-500 to-orange-700 rounded-2xl p-8 text-white shadow-xl flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="text-3xl font-black flex items-center mb-2 font-display">
              <Ticket className="w-8 h-8 mr-3 text-amber-200" />
              Digital Raffle Drum
            </h2>
            <p className="text-amber-100 max-w-xl text-lg">
              Paste your attendee list and project this on the big screen! A massive, high-stakes spinning ticket visualizer for your charity event or banquet.
            </p>
          </div>
          <div className="opacity-50 hidden md:block">
            <Ticket className="w-24 h-24" />
          </div>
        </div>
      )}

      <AnimatePresence>
        {gameState === 'setup' && (
          <motion.div
            key="setup"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="space-y-8"
          >
            <div className="grid md:grid-cols-2 gap-8">
              {/* Left Column: Input Form */}
              <Card className="p-6 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 shadow-xl">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                  <h3 className="font-bold text-lg flex items-center text-slate-700 dark:text-slate-300">
                    <Users className="w-5 h-5 mr-2 text-amber-500" />
                    1. Enter Participants
                  </h3>
                  <div className="flex items-center gap-2">
                    {hasActiveNode && (
                      <button
                        type="button"
                        onClick={() => setIsRosterModalOpen(true)}
                        className="text-xs font-semibold text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                      >
                        <Users className="w-3 h-3" />
                        Roster
                      </button>
                    )}
                    {hasActiveNode && <span className="text-slate-300 dark:text-slate-700">|</span>}
                    <button
                      type="button"
                      onClick={handleShuffle}
                      disabled={entryCount < 2}
                      className="text-xs font-semibold text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white disabled:opacity-40 hover:underline flex items-center gap-1"
                    >
                      <Shuffle className="w-3 h-3" />
                      Shuffle
                    </button>
                    <span className="text-slate-300 dark:text-slate-700">|</span>
                    <button
                      type="button"
                      onClick={() => setInputText(DEFAULT_PARTICIPANTS)}
                      className="text-xs font-semibold text-amber-600 dark:text-amber-400 hover:underline"
                    >
                      Sample
                    </button>
                    <span className="text-slate-300 dark:text-slate-700">|</span>
                    <button
                      type="button"
                      onClick={() => setInputText('')}
                      className="text-xs font-semibold text-slate-500 hover:text-red-500 hover:underline"
                    >
                      Clear
                    </button>
                  </div>
                </div>
                <p className="text-sm text-slate-500 mb-4">Paste your list of names below, one name per line.</p>
                <textarea
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  placeholder="Enter attendee names, one per line..."
                  className="w-full h-64 p-4 rounded-xl border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 focus:ring-2 focus:ring-amber-500 outline-none resize-none font-mono text-slate-900 dark:text-slate-100"
                />
                <div className="mt-2 flex items-center justify-between text-sm font-semibold text-slate-400">
                  <span>
                    Total entries: <strong className="text-slate-700 dark:text-slate-200">{entryCount}</strong>
                    {entryCount > 0 && uniqueCount !== entryCount && (
                      <span className="ml-1 text-xs text-slate-500">({uniqueCount} unique)</span>
                    )}
                  </span>
                  {entryCount > 0 && (
                    <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">Ready to spin</span>
                  )}
                </div>
              </Card>

              {/* Right Column: Drawing Action */}
              <Card className="p-6 bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 flex flex-col justify-center items-center text-center shadow-xl">
                <div className="w-24 h-24 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mb-6 shadow-inner">
                  <Ticket className="w-12 h-12 text-amber-500" />
                </div>
                <h3 className="text-2xl font-black mb-2 text-slate-800 dark:text-white">Ready to Draw?</h3>
                <p className="text-slate-500 mb-8 max-w-sm">
                  Hook up your laptop to the projector. The entire drawing happens securely in your browser—no data is saved to our servers.
                </p>
                <Button 
                  onClick={() => startRaffle()}
                  disabled={entryCount < 2}
                  className="w-full max-w-xs h-16 text-xl font-black bg-amber-500 hover:bg-amber-600 text-white shadow-lg shadow-amber-500/30"
                >
                  <Play className="w-6 h-6 mr-2 fill-current" />
                  START RAFFLE
                </Button>
                {entryCount < 2 ? (
                  <p className="text-xs font-semibold text-amber-600 dark:text-amber-400 mt-3">
                    Enter at least 2 participants to start
                  </p>
                ) : (
                  <p className="text-xs text-slate-400 dark:text-slate-500 mt-3 font-mono">
                    Press <kbd className="px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300">Space</kbd> to spin
                  </p>
                )}
              </Card>
            </div>

            {/* Drawn Winners podium / history */}
            {winnerHistory.length > 0 && (
              <Card className="p-6 bg-amber-50/50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800/50">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                  <h4 className="font-bold text-base flex items-center text-amber-900 dark:text-amber-300">
                    <Trophy className="w-4 h-4 mr-2 text-amber-500" />
                    Drawn Winners ({winnerHistory.length})
                  </h4>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={copyWinnerHistory}
                      className="text-xs font-semibold text-amber-700 dark:text-amber-400 hover:underline flex items-center gap-1"
                    >
                      {copiedWinners ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                      {copiedWinners ? 'Copied' : 'Copy List'}
                    </button>
                    <span className="text-amber-300 dark:text-amber-800">|</span>
                    <button
                      type="button"
                      onClick={() => setWinnerHistory([])}
                      className="text-xs font-semibold text-slate-500 hover:text-red-500 hover:underline flex items-center gap-1"
                    >
                      <Trash2 className="w-3 h-3" />
                      Clear
                    </button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {winnerHistory.map((name, i) => (
                    <Badge 
                      key={i} 
                      variant="gray"
                      className="bg-white dark:bg-slate-900 border-amber-300 dark:border-amber-700 text-slate-800 dark:text-slate-200 font-bold px-3 py-1.5"
                    >
                      <span className="text-amber-600 dark:text-amber-400 mr-1.5">#{winnerHistory.length - i}</span>
                      {name}
                    </Badge>
                  ))}
                </div>
              </Card>
            )}
          </motion.div>
        )}

        {(gameState === 'spinning' || gameState === 'winner') && (
          <motion.div
            key="spinning"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`w-full relative bg-slate-900 rounded-3xl shadow-2xl border-8 border-slate-800 overflow-hidden ${isFullscreen ? 'flex-1 min-h-[500px]' : 'h-[600px]'}`}
          >
            {/* The Visual Drum / Ticker Tape */}
            <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
              {/* Highlight window in the center */}
              <div className="absolute inset-x-0 h-48 bg-white/5 border-y-4 border-amber-500 z-20 flex items-center justify-between px-4 md:px-12 pointer-events-none">
                <div className="w-0 h-0 border-y-[20px] border-y-transparent border-l-[30px] border-l-amber-500 drop-shadow-md z-30"></div>
                <div className="w-0 h-0 border-y-[20px] border-y-transparent border-r-[30px] border-r-amber-500 drop-shadow-md z-30"></div>
              </div>

              {/* Ticker Tape */}
              <motion.div 
                key={`tape-${spinCount}`}
                className="flex flex-col gap-4 w-full max-w-2xl px-4 absolute top-1/2 z-10"
                initial={{ y: 0 }}
                animate={{ y: -(targetIndex * 128) }}
                transition={{ duration: 5, ease: [0.15, 0.85, 0.35, 1] }}
                onAnimationComplete={handleAnimationComplete}
                style={{
                  marginTop: '-56px' // Half of 112px ticket height
                }}
              >
                {visualTrack.map((name, idx) => (
                  <div 
                    key={idx} 
                    className="h-28 shrink-0 bg-white rounded-xl shadow-lg border-2 border-slate-200 flex items-center justify-center relative overflow-hidden"
                  >
                    {/* Ticket Stub lines */}
                    <div className="absolute left-10 inset-y-0 border-l-2 border-dashed border-slate-300"></div>
                    <div className="absolute right-10 inset-y-0 border-r-2 border-dashed border-slate-300"></div>
                    
                    <span className="text-2xl sm:text-3xl md:text-5xl font-black text-slate-800 tracking-tight uppercase truncate px-8 md:px-16 max-w-full">
                      {name}
                    </span>
                  </div>
                ))}
              </motion.div>
            </div>

            {/* Winner Overlay */}
            <AnimatePresence>
              {gameState === 'winner' && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="absolute inset-0 z-50 bg-black/85 backdrop-blur-sm flex flex-col items-center justify-center p-6 sm:p-8"
                >
                  <motion.div
                    initial={{ scale: 0.5, y: 50 }}
                    animate={{ scale: 1, y: 0 }}
                    transition={{ type: 'spring', bounce: 0.6 }}
                    className="text-center w-full max-w-3xl"
                  >
                    <Trophy className="w-24 h-24 sm:w-32 sm:h-32 text-yellow-400 mx-auto mb-6 drop-shadow-[0_0_30px_rgba(250,204,21,0.6)] animate-pulse" />
                    <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white uppercase tracking-widest mb-4">
                      Winner Selected!
                    </h3>
                    
                    <div className="bg-amber-500 rounded-3xl p-2 shadow-[0_0_50px_rgba(245,158,11,0.5)] my-4">
                      <div className="bg-slate-900 rounded-2xl px-6 sm:px-12 py-6 sm:py-8 border-4 border-amber-400 border-dashed">
                        <div className="text-3xl sm:text-5xl md:text-7xl font-black text-white uppercase tracking-tighter break-words max-w-full">
                          {winner}
                        </div>
                      </div>
                    </div>

                    <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                      <Button 
                        size="lg"
                        onClick={removeWinnerAndRerun}
                        className="h-14 sm:h-16 px-6 sm:px-8 text-base sm:text-xl font-bold bg-white text-slate-900 hover:bg-slate-200"
                      >
                        <RotateCcw className="w-5 h-5 mr-2" />
                        Remove & Spin Again
                      </Button>
                      <Button 
                        size="lg"
                        variant="secondary"
                        onClick={spinAgainKeepWinner}
                        className="h-14 sm:h-16 px-6 sm:px-8 text-base sm:text-xl font-bold bg-slate-800 text-white border-slate-700 hover:bg-slate-700"
                      >
                        Spin Again (Keep Winner)
                      </Button>
                      <Button 
                        size="lg"
                        variant="ghost"
                        onClick={resetRaffle}
                        className="h-14 sm:h-16 px-6 text-slate-400 hover:text-white"
                      >
                        Start Over
                      </Button>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Roster Picker Modal */}
      {hasActiveNode && (
        <NodeRosterPickerModal
          isOpen={isRosterModalOpen}
          onClose={() => setIsRosterModalOpen(false)}
          onImport={handleImportFromNode}
          title={`Import ${nodeName || 'Organization'} Attendees`}
          description={`Select members from ${nodeName} to enter into the digital raffle.`}
          confirmLabel="Add to Drum"
        />
      )}
    </div>
  );
}
